import * as THREE from 'three';
import { FLOWER_META, HONEY_COLOR, type CarryKind, type FlowerKind } from '../config';

const matCache = new Map<string, THREE.MeshStandardMaterial>();

function mat(key: string, color: number, opts: Partial<THREE.MeshStandardMaterialParameters> = {}) {
  const cacheKey = `${key}-${color}-${opts.roughness ?? 0.72}-${opts.metalness ?? 0.08}-${opts.emissive ?? 0}-${opts.emissiveIntensity ?? 0}`;
  let m = matCache.get(cacheKey);
  if (!m) {
    m = new THREE.MeshStandardMaterial({
      color,
      roughness: opts.roughness ?? 0.72,
      metalness: opts.metalness ?? 0.08,
      emissive: opts.emissive ?? 0x000000,
      emissiveIntensity: opts.emissiveIntensity ?? 0,
      flatShading: true,
    });
    matCache.set(cacheKey, m);
  }
  return m;
}

function mesh(geo: THREE.BufferGeometry, material: THREE.Material, cast = true, receive = true) {
  const m = new THREE.Mesh(geo, material);
  m.castShadow = cast;
  m.receiveShadow = receive;
  return m;
}

export function createPlayerMesh(): THREE.Group {
  const g = new THREE.Group();
  const body = mesh(new THREE.CapsuleGeometry(0.28, 0.55, 4, 8), mat('body', 0xffd7b0));
  body.position.y = 0.7;
  const apron = mesh(new THREE.BoxGeometry(0.55, 0.42, 0.2), mat('apron', 0xfff4d8));
  apron.position.set(0, 0.62, 0.12);
  const hair = mesh(new THREE.SphereGeometry(0.26, 10, 10), mat('hair', 0x5a3a28));
  hair.position.set(0, 1.18, 0);
  const hat = mesh(new THREE.CylinderGeometry(0.22, 0.28, 0.16, 10), mat('hat', 0x2f8a4a));
  hat.position.set(0, 1.38, 0);
  const brim = mesh(new THREE.CylinderGeometry(0.36, 0.36, 0.05, 12), mat('brim', 0x2f8a4a));
  brim.position.set(0, 1.3, 0);
  const basket = mesh(new THREE.TorusGeometry(0.22, 0.04, 6, 12), mat('basket', 0xc68642));
  basket.position.set(0.32, 0.55, 0);
  basket.rotation.x = Math.PI / 2;
  g.add(body, apron, hair, hat, brim, basket);
  return g;
}

export function createEmployeeMesh(variant = 0): THREE.Group {
  const g = new THREE.Group();
  const shirts = [0x3d6eaa, 0x5a9e6f, 0xc47a3a];
  const body = mesh(new THREE.CapsuleGeometry(0.26, 0.5, 4, 8), mat('emp-body', 0xf0c8a8));
  body.position.y = 0.65;
  const vest = mesh(new THREE.BoxGeometry(0.5, 0.4, 0.22), mat(`vest-${variant}`, shirts[variant % shirts.length]!));
  vest.position.set(0, 0.58, 0.08);
  const hair = mesh(new THREE.SphereGeometry(0.24, 10, 10), mat('emp-hair', 0x2c2118));
  hair.position.set(0, 1.1, 0);
  g.add(body, vest, hair);
  return g;
}

export function createCustomerMesh(seed: number): THREE.Group {
  const palette = [0xe8a0b0, 0x8ec5ff, 0xffd27a, 0xc5a3ff, 0x9be7c4, 0xffb38a];
  const shirt = palette[seed % palette.length]!;
  const g = new THREE.Group();
  const body = mesh(new THREE.CapsuleGeometry(0.24, 0.42, 4, 8), mat(`cust-${seed}`, 0xf2c9b0));
  body.position.y = 0.58;
  const top = mesh(new THREE.BoxGeometry(0.46, 0.34, 0.2), mat(`shirt-${seed}`, shirt));
  top.position.set(0, 0.52, 0.06);
  const head = mesh(new THREE.SphereGeometry(0.2, 10, 10), mat('head', 0xf2c9b0));
  head.position.set(0, 1.0, 0);
  g.add(body, top, head);
  return g;
}

export function createBeeMesh(): THREE.Group {
  const g = new THREE.Group();
  const body = mesh(new THREE.SphereGeometry(0.12, 8, 8), mat('bee', 0xf0b429, { roughness: 0.5 }));
  const stripe = mesh(new THREE.SphereGeometry(0.105, 8, 8), mat('stripe', 0x222222));
  stripe.scale.set(1.05, 0.7, 0.7);
  const wingL = mesh(new THREE.SphereGeometry(0.08, 8, 8), mat('wing', 0xdff6ff, { roughness: 0.3 }));
  wingL.scale.set(1.4, 0.25, 0.8);
  wingL.position.set(-0.1, 0.06, 0);
  const wingR = wingL.clone();
  wingR.position.x = 0.1;
  g.add(body, stripe, wingL, wingR);
  g.userData.wings = [wingL, wingR];
  return g;
}

export function createCarryItemMesh(kind: CarryKind): THREE.Group {
  const g = new THREE.Group();
  if (kind === 'honey') {
    const jar = mesh(
      new THREE.CylinderGeometry(0.12, 0.14, 0.22, 8),
      mat('honey-jar', HONEY_COLOR, { roughness: 0.4, metalness: 0.15 }),
    );
    const lid = mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.05, 8), mat('lid', 0x8b5a2b));
    lid.position.y = 0.14;
    g.add(jar, lid);
  } else if (kind === 'cash') {
    const bill = mesh(new THREE.BoxGeometry(0.28, 0.04, 0.16), mat('cash', 0x3ecf7a, { roughness: 0.55, metalness: 0.12 }));
    bill.position.y = 0.05;
    g.add(bill);
  } else {
    const meta = FLOWER_META[kind];
    const bloom = mesh(
      new THREE.SphereGeometry(0.14, 8, 8),
      mat(`bloom-${kind}`, meta.color, {
        roughness: 0.5,
        emissive: kind === 'crystal' ? meta.color : 0x000000,
        emissiveIntensity: kind === 'crystal' ? 0.35 : 0,
      }),
    );
    bloom.position.y = 0.1;
    const wrap = mesh(new THREE.ConeGeometry(0.12, 0.22, 6), mat(`wrap-${kind}`, 0xf7f1df));
    wrap.position.y = -0.05;
    wrap.rotation.x = Math.PI;
    if (kind === 'sunflower') {
      const center = mesh(new THREE.SphereGeometry(0.07, 8, 8), mat('sun-center', 0x5a3a18));
      center.position.y = 0.12;
      g.add(bloom, center, wrap);
    } else {
      g.add(bloom, wrap);
    }
  }
  g.scale.setScalar(0.95);
  return g;
}

export function createBushMesh(kind: FlowerKind, ready = true): THREE.Group {
  const meta = FLOWER_META[kind];
  const g = new THREE.Group();
  const base = mesh(new THREE.SphereGeometry(0.55, 10, 10), mat(`bush-${kind}`, meta.bushColor));
  base.scale.set(1.15, 0.75, 1.15);
  base.position.y = 0.35;
  g.add(base);

  const flowerCount = ready ? 3 + Math.min(meta.tier, 2) : 1;
  for (let i = 0; i < flowerCount; i++) {
    const a = (i / flowerCount) * Math.PI * 2;
    const r = 0.28 + (i % 3) * 0.05;
    const bloom = mesh(
      new THREE.SphereGeometry(ready ? 0.13 : 0.08, 6, 6),
      mat(`f-${kind}`, ready ? meta.color : 0x9ad0a8, {
        emissive: kind === 'crystal' && ready ? meta.color : 0x000000,
        emissiveIntensity: kind === 'crystal' && ready ? 0.4 : 0,
      }),
    );
    bloom.position.set(Math.cos(a) * r, 0.45 + (i % 2) * 0.1, Math.sin(a) * r);
    g.add(bloom);
  }

  if (!ready) g.scale.setScalar(0.55);
  g.userData.kind = kind;
  g.userData.ready = ready;
  return g;
}

export function createHiveMesh(level = 0): THREE.Group {
  const g = new THREE.Group();
  const scale = 1 + level * 0.08;
  const body = mesh(new THREE.CylinderGeometry(0.7, 0.85, 1.1, 8), mat('hive', 0xc68642));
  body.position.y = 0.55;
  const top = mesh(new THREE.CylinderGeometry(0.55, 0.7, 0.25, 8), mat('hive-top', 0xa86b32));
  top.position.y = 1.2;
  const door = mesh(new THREE.BoxGeometry(0.28, 0.28, 0.08), mat('hive-door', 0x5a3a1a), false, true);
  door.position.set(0, 0.35, 0.78);
  g.add(body, top, door);
  g.scale.setScalar(scale);
  return g;
}

export function createCounterMesh(level = 0): THREE.Group {
  const g = new THREE.Group();
  const w = 4.2 + level * 0.35;
  const desk = mesh(new THREE.BoxGeometry(w, 0.7, 1.1), mat('desk', 0x8b5a2b));
  desk.position.y = 0.4;
  const cloth = mesh(new THREE.BoxGeometry(w - 0.2, 0.08, 1.0), mat('cloth', 0xfff0d6));
  cloth.position.y = 0.78;
  const sign = mesh(new THREE.BoxGeometry(1.6, 0.5, 0.08), mat('sign', 0x2f8a4a));
  sign.position.set(0, 1.25, 0);
  const canopy = mesh(new THREE.BoxGeometry(w + 0.4, 0.1, 1.5), mat('awning', 0xff8fab));
  canopy.position.set(0, 1.7, 0.1);
  g.add(desk, cloth, sign, canopy);
  return g;
}

export function createStoreroomMesh(level = 0): THREE.Group {
  const g = new THREE.Group();
  const s = 3.2 + level * 0.15;
  const floor = mesh(new THREE.BoxGeometry(s, 0.12, s), mat('store-floor', 0xd9c3a0), false, true);
  floor.position.y = 0.06;
  const wallA = mesh(new THREE.BoxGeometry(s, 1.4, 0.12), mat('store-wall', 0xe8d8b8), false, true);
  wallA.position.set(0, 0.7, -s / 2);
  const wallB = mesh(new THREE.BoxGeometry(0.12, 1.4, s), mat('store-wall2', 0xe0d0b0), false, true);
  wallB.position.set(-s / 2, 0.7, 0);
  const crate = mesh(new THREE.BoxGeometry(1.1, 0.7, 0.9), mat('crate', 0xb07a3a));
  crate.position.set(0.5, 0.4, 0.3);
  const crate2 = mesh(new THREE.BoxGeometry(0.9, 0.55, 0.8), mat('crate2', 0x9a6a32));
  crate2.position.set(-0.5, 0.32, 0.4);
  g.add(floor, wallA, wallB, crate, crate2);
  return g;
}

export function createUpgradeKioskMesh(): THREE.Group {
  const g = new THREE.Group();
  const base = mesh(new THREE.CylinderGeometry(0.7, 0.85, 0.3, 10), mat('kiosk-base', 0xffe08a));
  base.position.y = 0.15;
  const pole = mesh(new THREE.CylinderGeometry(0.08, 0.08, 1.6, 8), mat('kiosk-pole', 0xc68642));
  pole.position.y = 1.0;
  const board = mesh(new THREE.BoxGeometry(1.4, 1.0, 0.12), mat('kiosk-board', 0x2f6b4a));
  board.position.y = 1.7;
  const star = mesh(
    new THREE.SphereGeometry(0.18, 8, 8),
    mat('kiosk-star', 0xffd66b, { emissive: 0xffaa00, emissiveIntensity: 0.45 }),
  );
  star.position.y = 2.35;
  g.add(base, pole, board, star);
  return g;
}

export function createHoneyPotMesh(): THREE.Group {
  return createCarryItemMesh('honey');
}

export function createCashStackMesh(height = 4): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < height; i++) {
    const bill = mesh(new THREE.BoxGeometry(0.32, 0.05, 0.18), mat('cash-stack', 0x3ecf7a));
    bill.position.y = 0.05 + i * 0.06;
    bill.rotation.y = (i % 2) * 0.15;
    g.add(bill);
  }
  return g;
}

export function createTreeMesh(seed: number): THREE.Group {
  const g = new THREE.Group();
  const trunk = mesh(new THREE.CylinderGeometry(0.12, 0.18, 1.1, 6), mat(`trunk-${seed}`, 0x6b4423));
  trunk.position.y = 0.55;
  const canopy = mesh(
    new THREE.SphereGeometry(0.7, 10, 10),
    mat(`canopy-${seed}`, seed % 2 ? 0x2f8a4a : 0x3aa35a),
  );
  canopy.position.y = 1.35;
  g.add(trunk, canopy);
  return g;
}

export function createGround(size: number): THREE.Mesh {
  const geo = new THREE.CircleGeometry(size, 64);
  const m = mesh(geo, mat('ground', 0x4fa86a, { roughness: 0.95 }), false, true);
  m.rotation.x = -Math.PI / 2;
  return m;
}

export function createPathTile(w: number, d: number): THREE.Mesh {
  const m = mesh(new THREE.BoxGeometry(w, 0.06, d), mat('path', 0xd8c39a, { roughness: 0.9 }), false, true);
  m.position.y = 0.03;
  return m;
}

export function createOrderBubble(kind: FlowerKind): THREE.Group {
  const g = new THREE.Group();
  const bg = mesh(new THREE.SphereGeometry(0.28, 10, 10), mat('bubble', 0xfff8ea, { roughness: 0.4 }));
  bg.position.y = 1.55;
  const icon = createCarryItemMesh(kind);
  icon.position.y = 1.55;
  icon.scale.setScalar(0.85);
  g.add(bg, icon);
  g.userData.kind = kind;
  return g;
}

export function createGuideArrow(): THREE.Group {
  const g = new THREE.Group();
  const shaft = mesh(new THREE.BoxGeometry(0.2, 0.05, 0.55), mat('arrow', 0x7dffb0, { emissive: 0x2a8f5a, emissiveIntensity: 0.35 }));
  shaft.position.y = 0.08;
  const head = mesh(new THREE.ConeGeometry(0.18, 0.28, 6), mat('arrow-h', 0x7dffb0, { emissive: 0x2a8f5a, emissiveIntensity: 0.35 }));
  head.rotation.x = -Math.PI / 2;
  head.position.set(0, 0.08, -0.4);
  g.add(shaft, head);
  return g;
}

export function createFenceSegment(): THREE.Group {
  const g = new THREE.Group();
  const post = mesh(new THREE.BoxGeometry(0.1, 0.7, 0.1), mat('fence', 0xc9a06a));
  post.position.y = 0.35;
  const rail = mesh(new THREE.BoxGeometry(1.2, 0.08, 0.06), mat('rail', 0xd4b07a));
  rail.position.set(0.5, 0.45, 0);
  g.add(post, rail);
  return g;
}
